import { type ActorStatus } from '../api/client'
import { bskyPostUrl } from '../utils/handle'
import { formatLocal, formatWeekday, relativeTime } from '../utils/time'

type Props = {
  did: string
  handle?: string
  displayName?: string
  description?: string
  avatar?: string
  status: ActorStatus
  labels?: string[]
  verb?: string
  verbedAt?: string
}

const STATUS_BADGE: Record<ActorStatus, { label: string; cls: string; tip: string }> = {
  loading: {
    label: 'checking…',
    cls: 'border-white/10 bg-white/5 text-ink-300 animate-pulse',
    tip: 'looking the account up on the bsky AppView',
  },
  active: {
    label: 'active',
    cls: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200',
    tip: 'account is live and indexed by the bsky AppView',
  },
  unavailable: {
    label: 'unavailable',
    cls: 'border-amber-400/30 bg-amber-400/10 text-amber-200',
    tip: 'AppView did not return a profile — likely deleted, deactivated, or suspended',
  },
}

export function UserCard({
  did,
  handle,
  displayName,
  description,
  avatar,
  status,
  labels,
  verb,
  verbedAt,
}: Props) {
  const badge = STATUS_BADGE[status]
  const verbedDate = verbedAt ? new Date(verbedAt) : null
  const muted = status !== 'active'
  const profileUrl = handle
    ? `https://bsky.app/profile/${handle}`
    : `https://bsky.app/profile/${did}`
  const initials = (displayName ?? handle ?? did).slice(0, 1).toUpperCase()
  const takendown = labels?.some((l) => l === '!takendown' || l === '!suspended')

  return (
    <details className={`glass overflow-hidden ${muted ? 'opacity-80' : ''}`}>
      <summary className="cursor-pointer list-none px-4 py-3">
        <div className="flex items-start gap-3">
          {avatar ? (
            <img
              src={avatar}
              alt=""
              loading="lazy"
              className={`h-10 w-10 flex-shrink-0 rounded-full object-cover ${
                muted ? 'grayscale' : ''
              }`}
            />
          ) : (
            <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-white/10 bg-gradient-to-br from-sky-500/30 to-violet-500/30 text-sm font-bold">
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="truncate font-semibold">
                {displayName ?? handle ?? did}
              </span>
              {handle ? (
                <span className="truncate text-xs text-ink-300">@{handle}</span>
              ) : null}
              <span
                className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${badge.cls}`}
                title={badge.tip}
              >
                {badge.label}
              </span>
              {takendown ? (
                <span
                  className="rounded-full border border-rose-400/30 bg-rose-400/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-rose-200"
                  title="account is suspended / taken down"
                >
                  takendown
                </span>
              ) : null}
            </div>
            {description ? (
              <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-sm text-ink-200">
                {description}
              </p>
            ) : null}
          </div>
          <div className="flex max-w-[40%] flex-shrink-0 flex-col items-end gap-1 text-right">
            {verb && verbedDate ? (
              <>
                <div className="text-[10px] uppercase tracking-widest accent-text">{verb}</div>
                <div className="text-xs text-ink-200" title={formatLocal(verbedDate)}>
                  {formatWeekday(verbedDate)} • {relativeTime(verbedDate)}
                </div>
                <div className="text-[10px] text-ink-300">{formatLocal(verbedDate)}</div>
              </>
            ) : null}
          </div>
        </div>
      </summary>

      <div className="space-y-2 border-t border-white/5 px-4 py-3 text-sm text-ink-200">
        {description ? (
          <p className="whitespace-pre-wrap">{description}</p>
        ) : (
          <p className="text-ink-300">No bio.</p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          <a
            href={profileUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs accent-text hover:underline"
          >
            Open profile in Bluesky ↗
          </a>
          <span className="font-mono text-[10px] text-ink-400" title={did}>
            {did}
          </span>
        </div>
      </div>
    </details>
  )
}

// keep helpers in scope so unused-import warnings don't fire elsewhere
void bskyPostUrl
